/**
 * Post-Stripe success page — polls payment status, fires confetti on paid,
 * then lands the user on Billing (or /onboarding when they're mid-wizard).
 */
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Check, Waves } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BillingSuccessPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('polling');
  const [kind, setKind] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const r = await axios.get(`${API}/api/payments/checkout/status/${sessionId}`, { withCredentials: true });
        setKind(r.data.kind);
        if (r.data.payment_status === 'paid') {
          await refreshUser();
          setState('paid');
          setTimeout(() => {
            nav(r.data.kind === 'subscription' ? '/onboarding' : '/billing', { replace: true });
          }, 2200);
          return;
        }
        if (r.data.status === 'expired') { setState('expired'); return; }
      } catch (err) {
        // Stripe reconciliation can take a moment; keep polling until we hit
        // the cap. Log for debugging but never surface the noise to the user.
        if (process.env.NODE_ENV !== 'production') console.debug('billing success poll retry:', err?.response?.status || err?.message);
      }
      if (tries < 12) setTimeout(poll, 1500);
      else setState('timeout');
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white px-4" data-testid="billing-success-page">
      <div className="max-w-md w-full text-center rounded-2xl border border-[#1f1f1f] bg-[#121212] p-10" style={{ boxShadow: '0 30px 80px rgba(0,212,255,0.15)' }}>
        <Waves className="h-7 w-7 text-[#00D4FF] mx-auto mb-3" />
        {state === 'polling' && (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-[#00D4FF] mx-auto my-5" />
            <p className="text-sm text-gray-400">Confirming your payment…</p>
          </>
        )}
        {state === 'paid' && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#00D4FF]/15 flex items-center justify-center mx-auto my-4 animate-pulse">
              <Check className="h-8 w-8 text-[#00D4FF]" />
            </div>
            <h1 className="text-2xl font-black mb-1" data-testid="billing-success-title">Payment successful</h1>
            <p className="text-xs text-gray-400">{kind === 'subscription' ? 'Your subscription is active — next stop, your first Wave.' : 'Your wallet has been topped up. Redirecting…'}</p>
          </>
        )}
        {state === 'expired' && (
          <>
            <p className="text-sm text-red-400 my-5">Session expired. Please try again.</p>
            <Link to="/billing" className="text-xs text-[#00D4FF] hover:underline">Back to billing</Link>
          </>
        )}
        {(state === 'timeout' || state === 'error') && (
          <>
            <p className="text-sm text-gray-400 my-5">Still processing. Refresh in a moment to see updates.</p>
            <Link to="/billing" className="text-xs text-[#00D4FF] hover:underline">Go to billing</Link>
          </>
        )}
      </div>
    </div>
  );
}
